import { Controller } from "@hotwired/stimulus";

// Allowed MIME types (must match AttachmentUploader validations)
const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.presentation",
  "application/zip",
  "application/x-7z-compressed",
  "application/x-rar-compressed",
  "application/x-compressed",
  "application/x-zip-compressed",
  "multipart/x-zip",
  "application/x-zip",
  "application/octet-stream",
  "application/x-rar",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "text/plain",
  "text/csv",
]);

const ALLOWED_EXTENSIONS = new Set([
  "doc", "docx", "xls", "xlsx", "ppt", "pptx",
  "pdf", "zip", "rar", "7z", "txt", "csv",
  "jpg", "jpeg", "png", "webp",
]);

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const FILE_ICONS = {
  pdf: "📄", doc: "📝", docx: "📝", xls: "📊", xlsx: "📊",
  ppt: "📎", pptx: "📎", zip: "📦", rar: "📦", "7z": "📦",
  txt: "📃", csv: "📃", jpg: "🖼️", jpeg: "🖼️", png: "🖼️",
  webp: "🖼️", gif: "🖼️",
};

export default class extends Controller {
  static targets = [
    "fileInput",
    "cachedField",
    "progressBar",
    "progressFill",
    "progressText",
    "progressContainer",
    "successMessage",
    "errorMessage",
    "retryButton",
    "cancelButton",
    "fileName",
    "dropzone",
  ];

  static values = {
    presignUrl: { type: String, default: "/attachments/presign" },
    maxSize: { type: Number, default: MAX_FILE_SIZE },
  };

  connect() {
    this.xhr = null;
    this.currentFile = null;
  }

  disconnect() {
    this.abortUpload();
  }

  // --- Drag & Drop ---

  dragover(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("upload-dropzone--active");
    }
  }

  dragleave(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("upload-dropzone--active");
    }
  }

  drop(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("upload-dropzone--active");
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  // --- File Selection ---

  selectFile(event) {
    const file = event.target.files?.[0];
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file) {
    // Validate extension
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      this.showError(`不支持的文件格式: .${ext}`);
      return;
    }

    // Validate size
    if (file.size > this.maxSizeValue) {
      const maxMB = Math.round(this.maxSizeValue / 1024 / 1024);
      this.showError(`文件太大，最大 ${maxMB}MB`);
      return;
    }

    this.currentFile = file;
    this.uploadFile(file);
  }

  // --- Upload ---

  async uploadFile(file) {
    this.resetState();
    this.showProgress();

    try {
      // 1. Get presign / upload info from server
      const presignData = await this.fetchPresign(file);

      if (presignData.method === "upload") {
        // Fallback: POST file to Rails upload endpoint (non-COS environments)
        const uploadedData = await this.postFile(presignData, file);
        this.cachedFieldTarget.value = JSON.stringify(uploadedData);
      } else {
        // Direct upload: PUT file to COS via presigned URL
        await this.putFile(presignData, file);
        this.cachedFieldTarget.value = JSON.stringify({
          id: presignData.key,
          storage: "cache",
          metadata: {
            filename: file.name,
            size: file.size,
            mime_type: file.type || "application/octet-stream",
          },
        });
      }

      this.showSuccess(file.name, file.size);
    } catch (error) {
      if (error.name === "AbortError" || error.message === "Upload cancelled") {
        return;
      }
      console.error("Direct upload failed:", error);
      this.showError(error.message || "上传失败，请重试");
    }
  }

  async fetchPresign(file) {
    const params = new URLSearchParams({
      filename: file.name,
      type: file.type || "application/octet-stream",
    });

    const response = await fetch(`${this.presignUrlValue}?${params}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 302) {
        throw new Error("请先登录后再上传文件");
      }
      throw new Error(`预签名请求失败 (${response.status})`);
    }

    return await response.json();
  }

  // Direct PUT to COS via presigned URL
  putFile(presignData, file) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.xhr = xhr;

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          this.updateProgress(percent);
        }
      });

      xhr.addEventListener("load", () => {
        this.xhr = null;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`上传失败 (${xhr.status})`));
        }
      });

      xhr.addEventListener("error", () => {
        this.xhr = null;
        reject(new Error("网络错误，请检查网络连接后重试"));
      });

      xhr.addEventListener("abort", () => {
        this.xhr = null;
        reject(new Error("Upload cancelled"));
      });

      xhr.open("PUT", presignData.url);

      const headers = presignData.headers || {};
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.send(file);
    });
  }

  // Fallback: POST multipart to Rails upload endpoint
  postFile(presignData, file) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.xhr = xhr;

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          this.updateProgress(percent);
        }
      });

      xhr.addEventListener("load", () => {
        this.xhr = null;
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error("服务器响应格式错误"));
          }
        } else {
          reject(new Error(`上传失败 (${xhr.status})`));
        }
      });

      xhr.addEventListener("error", () => {
        this.xhr = null;
        reject(new Error("网络错误，请检查网络连接后重试"));
      });

      xhr.addEventListener("abort", () => {
        this.xhr = null;
        reject(new Error("Upload cancelled"));
      });

      const csrfToken =
        document.querySelector('meta[name="csrf-token"]')?.content || "";
      const formData = new FormData();
      formData.append("file", file);

      xhr.open("POST", presignData.url);
      xhr.setRequestHeader("X-CSRF-Token", csrfToken);
      xhr.send(formData);
    });
  }

  // --- Retry / Cancel ---

  retry() {
    if (this.currentFile) {
      this.uploadFile(this.currentFile);
    }
  }

  cancel() {
    this.abortUpload();
    this.resetState();
    this.hideProgress();
  }

  abortUpload() {
    if (this.xhr) {
      this.xhr.abort();
      this.xhr = null;
    }
  }

  // --- UI Helpers ---

  resetState() {
    this.hideError();
    this.hideSuccess();
    if (this.hasRetryButtonTarget) this.retryButtonTarget.classList.add("hidden");
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.classList.add("hidden");
  }

  showProgress() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove("hidden");
    }
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.remove("hidden");
    }
    this.updateProgress(0);
  }

  hideProgress() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add("hidden");
    }
  }

  updateProgress(percent) {
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${percent}%`;
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percent}%`;
    }
  }

  showSuccess(fileName, fileSize) {
    this.hideProgress();
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.classList.add("hidden");

    if (this.hasSuccessMessageTarget) {
      const ext = fileName.split(".").pop()?.toLowerCase();
      const icon = FILE_ICONS[ext] || "📎";
      const sizeStr = this.formatFileSize(fileSize);

      this.successMessageTarget.innerHTML =
        `<span class="upload-success-icon">✓</span> ` +
        `<span class="upload-file-icon">${icon}</span> ` +
        `<span class="upload-filename-text">${this.escapeHtml(fileName)}</span> ` +
        `<span class="upload-filesize-text">(${sizeStr})</span>`;
      this.successMessageTarget.classList.remove("hidden");
    }

    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = fileName;
    }

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("upload-dropzone--uploaded");
    }
  }

  hideSuccess() {
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.textContent = "";
      this.successMessageTarget.classList.add("hidden");
    }
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("upload-dropzone--uploaded");
    }
  }

  showError(message) {
    this.hideProgress();
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.classList.add("hidden");

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorMessageTarget.classList.remove("hidden");
    }
    if (this.hasRetryButtonTarget) {
      this.retryButtonTarget.classList.remove("hidden");
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "";
      this.errorMessageTarget.classList.add("hidden");
    }
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
